import { observer } from "mobx-react";
import { Tag } from "primereact/tag";
import React, { useContext, useRef } from "react";
import { useLocStore } from "../../wsLocalization";
import { LocContext } from "../../wsLocalization/LocContext";
import { getRedirectProps } from "../../WSUtils/urlUtils";
import { useBreakpointObservable } from "../../WSUtils/useBreakpoint";
import { UserInfoContext } from "../../WSUtils/UserInfoContext";
import { concatClassnames } from "../../WSUtils/util";
import { withWSErrorContainer } from "../../WSUtils/withWSErrorContainer";
import { WSFormGrid } from "../../WSUtils/WSFormGrid";
import { CVEditStepType } from "../Steps/Step";
import { CVEditApplicationStep } from "./ApplicationStep/CVEditApplicationStep";
import { CVEditApplyForPositionButton } from "./CVEditApplyForPositionButton";
import { CVEditContext, useCVEditContext } from "./CVEditContext";
import { CVEditNextStepButton } from "./CVEditNextStepButton";
import { CVEditStepSelector } from "./CVEditStepSelector";
import { CVEditStyledCard } from "./CVEditStyles";
import { CVEditDetailsStep } from "./DetailsStep/CVEditDetailsStep";
import { CVEditDocumentStep } from "./DocumentStep/CVEditDocumentsStep";
import { CVEditExperienceStep } from "./ExperienceStep/CVEditExperienceStep";
import { CVEditQualificationsStep } from "./QualificationsStep/CVEditQualificationsStep";
import { CVEditQuestionsStep } from "./QuestionsStep/CVEditQuestionsStep";
import { useLatestQuery } from "../../WSUtils/useLatestQuery";

export const CVEdit = withWSErrorContainer(
  observer(() => {
    const userInfo = useContext(UserInfoContext);

    // measuring body instead of local div to prevent layoutshift
    const containerRef = useRef<HTMLElement>(document.body);
    const layoutClassName = useBreakpointObservable(containerRef, (width) => {
      if (width >= 600) return "cve-l";
      if (width >= 460) return "cve-m";
      return "cve-s";
    });

    const cvEditContext = useCVEditContext(userInfo, layoutClassName);

    return (
      <CVEditContext.Provider value={cvEditContext}>
        <div>
          <CVEditStyledCard
            className={concatClassnames([
              "cve-container",
              layoutClassName.get(),
              cvEditContext.alreadyAppliedToPosition &&
                "cve-container-already-applied",
            ])}
            header={<Header />}
          >
            <Content />
          </CVEditStyledCard>
        </div>
      </CVEditContext.Provider>
    );
  })
);

const Header = observer(() => {
  const L = useLocStore(LocContext.CV);
  const cvEditContext = useContext(CVEditContext);
  const userInfo = useContext(UserInfoContext);

  const { data: connectedProfiles } = useLatestQuery(
    ["authAPI.connectedProfiles"],
    () => userInfo.fullAPI.authAPI.connectedProfiles({ userInfo })
  );

  if (!cvEditContext.dataFromServer.initialized) return null;

  const job = cvEditContext.dataFromServer.job;

  if (cvEditContext.alreadyAppliedToPosition) {
    return null;
  }

  const profile = connectedProfiles?.result?.length
    ? connectedProfiles?.result[0]
    : null;

  return (
    <div className="cve-main-header">
      <div className="cvp-h2">
        {!cvEditContext.jobApplicationMode &&
          L.id("6d75a6f82df6").loc("Min CV")}
        {!!cvEditContext.jobApplicationMode &&
          L.id("89321dddc7d8").loc("Stillingssøknad")}
      </div>
      <div
        className={concatClassnames([
          "cve-main-header-subtext",
          cvEditContext.jobApplicationMode &&
            "cve-main-header-subtext-important",
        ])}
      >
        {/* {!cvEditContext.jobApplicationMode &&
          L.id("1476fc6b83ea").loc("Oppdater CVen for å bli lagt merke til")} */}

        {!cvEditContext.jobApplicationMode && profile && (
          <>
            <span>
              {L.id("cc28136831d5").split(
                "Du er tilkoblet Webtemp konto {0} ({1}).",
                [
                  `${profile.details.fornavn} ${profile.details.etternavn}`.trim(),
                  profile.details.email,
                ]
              )}
            </span>{" "}
            <a
              {...getRedirectProps("/profile", {
                mergeQs: { connect: "1", cvpstep: null },
                userInfo,
              })}
            >
              {L.id("dd5edfd696a8").loc("Bytt konto")}
            </a>
          </>
        )}
        {!!cvEditContext.jobApplicationMode &&
          L.id("1015ef15cb8b").split("Du søker nå på stillingen {0}", [
            <a
              className="cve-subtext-link"
              {...getRedirectProps(cvEditContext.applyingForAdUrl, {
                userInfo,
                mergeQs: null,
              })}
            >
              {job.jobText.jobtitle}
            </a>,
          ])}
      </div>
    </div>
  );
});

const AlreadyAppliedContent = () => {
  const cvEditContext = useContext(CVEditContext);
  const userInfo = useContext(UserInfoContext);
  const L = useLocStore(LocContext.CV);

  const getAdLinkText = () => {
    const job = cvEditContext.dataFromServer.job;
    if (!job?.ok) return null;

    return `${job.extra.refNumber} ${job.jobText.jobtitle}`;
  };

  if (cvEditContext.appliedToPositionThisSession) {
    return (
      <div className="cve-already-applied-content">
        <div className="cvp-h2">{L.id("89321dddc7d8").loc("Takk!")}</div>

        <div className="cve-already-applied-subtext">
          {L.id("ccd21b0d5b49").split(
            "Din søknad på stilling {0} er mottatt.",
            [
              <a
                className="cve-subtext-link"
                {...getRedirectProps(cvEditContext.applyingForAdUrl, {
                  userInfo,
                  mergeQs: null,
                })}
              >
                {getAdLinkText()}
              </a>,
            ]
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="cve-already-applied-content">
      <Tag
        severity="success"
        className="cve-already-applied-badge"
        icon="pi pi-check"
        value={L.id("b203e24665ad").loc(
          "Du har allerede søkt på denne stillingen"
        )}
      />
      {window.history.length > 0 && (
        <a
          className="cve-subtext-link"
          onClick={() => {
            window.history.back();
          }}
        >
          {L.id("2e508e1f8423").loc("Tilbake")}
        </a>
      )}
    </div>
  );
};

const Content = observer(() => {
  const cvEditContext = useContext(CVEditContext);
  const userInfo = useContext(UserInfoContext);

  if (!cvEditContext.dataFromServer.initialized) return null;

  if (cvEditContext.alreadyAppliedToPosition) {
    return <AlreadyAppliedContent />;
  }

  return (
    <div className="cve-content">
      <CVEditStepSelector />

      <div className="cve-steps-and-buttons-container">
        <div className="cve-steps-container">
          {cvEditContext.currentStep.value === CVEditStepType.Details && (
            <CVEditDetailsStep />
          )}
          {cvEditContext.currentStep.value === CVEditStepType.Experience && (
            <CVEditExperienceStep />
          )}
          {cvEditContext.currentStep.value ===
            CVEditStepType.Qualifications && <CVEditQualificationsStep />}
          {cvEditContext.currentStep.value === CVEditStepType.Documents && (
            <CVEditDocumentStep />
          )}
          {cvEditContext.currentStep.value === CVEditStepType.QuestionForm && (
            <CVEditQuestionsStep />
          )}
          {cvEditContext.currentStep.value === CVEditStepType.Application && (
            <CVEditApplicationStep />
          )}
        </div>

        <WSFormGrid float className="cve-button-bar cve-main-button-bar">
          <CVEditNextStepButton />
          <CVEditApplyForPositionButton />
        </WSFormGrid>
      </div>
    </div>
  );
});
